import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import type {
  ApiClientConfig,
  ApiResponse,
  ApiError,
  RequestInterceptor,
  ResponseInterceptor,
  ErrorInterceptor,
  LoginRequest,
  LoginResponse,
  UserInfo,
} from './types';

class ApiClient {
  private instance: AxiosInstance;
  private authBaseURL: string;
  private authHeaders: Record<string, string>;
  private isRefreshing = false;
  private refreshTokenPromise: Promise<void> | null = null;

  constructor(config: ApiClientConfig) {
    this.authBaseURL = config.authBaseURL || config.baseURL;
    this.authHeaders = config.authHeaders || {};
    this.instance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 10000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
      withCredentials: config.withCredentials || false,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // 请求拦截器（async：支持请求前主动刷新 token）
    this.instance.interceptors.request.use(
      async config => {
        // 运行时动态读取网关地址（优先 config.js，fallback 到 env），确保 config.js 加载后生效
        const configGateway =
          (globalThis as any)._intellix_server_config?.gatewayBaseUrl ||
          (globalThis as any)._template_app_server_config?.gatewayBaseUrl;
        const runtimeGateway =
          (typeof configGateway === 'string' && configGateway.startsWith('http') ? configGateway : null) ||
          import.meta.env.VITE_GATEWAY_BASE_URL ||
          'https://gateway-dev.aialign.cn';
        const base = runtimeGateway.endsWith('/')
          ? runtimeGateway.slice(0, -1)
          : runtimeGateway;
        this.instance.defaults.baseURL = `${base}/aialign-intellix-core`;
        this.authBaseURL = `${base}/aialign-auth-service`;

        // 主动检查 token 是否即将过期，若是则提前刷新
        if (this.isTokenExpiringSoon()) {
          try {
            await this.refreshAccessToken();
          } catch {
            this.handleUnauthorized();
            return Promise.reject(new Error('Token refresh failed'));
          }
        }

        // 仅在请求未携带 Authorization 时注入 Bearer token
        if (!config.headers.Authorization) {
          const token = this.getAuthToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // 添加请求时间戳
        (config as any).metadata = { startTime: Date.now() };

        console.log(
          `🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`
        );
        return config;
      },
      error => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        const duration =
          Date.now() - ((response.config as any).metadata?.startTime || 0);
        console.log(
          `✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`
        );

        // 检查业务状态码 - 修复：根据实际的响应格式判断成功状态
        if (response.data && response.data.code !== undefined) {
          // 如果 code 不是 200，认为是业务错误
          if (response.data.code !== 200) {
            const error: ApiError = {
              code: response.data.code,
              message: response.data.message,
              details: response.data,
            };
            return Promise.reject(error);
          }
        }

        return response;
      },
      error => {
        const duration =
          Date.now() - ((error.config as any)?.metadata?.startTime || 0);
        console.error(
          `❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url} (${duration}ms)`,
          error
        );

        // 处理不同类型的错误
        if (error.response) {
          // 服务器响应错误
          const apiError: ApiError = {
            code: error.response.status,
            message: error.response.data?.message || error.message,
            details: error.response.data,
          };

          // 处理特定状态码
          switch (error.response.status) {
            case 401:
              this.handleUnauthorized();
              break;
            case 403:
              console.warn('Access forbidden');
              break;
            case 500:
              console.error('Server internal error');
              break;
          }

          return Promise.reject(apiError);
        } else if (error.request) {
          // 网络错误
          const networkError: ApiError = {
            code: 0,
            message: 'Network error, please check your connection',
          };
          return Promise.reject(networkError);
        } else {
          // 其他错误
          const unknownError: ApiError = {
            code: -1,
            message: error.message || 'Unknown error',
          };
          return Promise.reject(unknownError);
        }
      }
    );
  }

  private getAuthToken(): string | null {
    if (typeof globalThis !== 'undefined' && (globalThis as any).localStorage) {
      return (globalThis as any).localStorage.getItem('auth_token');
    }
    return null;
  }

  private getRefreshToken(): string | null {
    if (typeof globalThis !== 'undefined' && (globalThis as any).localStorage) {
      return (globalThis as any).localStorage.getItem('refresh_token');
    }
    return null;
  }

  // 解码 JWT payload 中的 exp 字段（Unix 秒时间戳）
  private decodeJwtExp(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(payload));
      return typeof decoded.exp === 'number' ? decoded.exp : null;
    } catch {
      return null;
    }
  }

  // 判断当前 token 是否即将过期（默认提前 7200 秒即 2 小时判断）
  private isTokenExpiringSoon(thresholdSeconds = 7200): boolean {
    const token = this.getAuthToken();
    if (!token) return false;
    const exp = this.decodeJwtExp(token);
    if (exp === null) return false;
    const nowSeconds = Math.floor(Date.now() / 1000);
    return nowSeconds >= exp - thresholdSeconds;
  }

  // 调用 OAuth2 refresh_token 端点获取新 token（防并发：多个请求同时触发时只发一次）
  private async refreshAccessToken(): Promise<void> {
    if (this.isRefreshing && this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh_token available');
    }

    this.isRefreshing = true;
    this.refreshTokenPromise = (async () => {
      try {
        const params = new URLSearchParams();
        params.append('grant_type', 'refresh_token');
        params.append('refresh_token', refreshToken);

        const response = await axios.post(
          `${this.authBaseURL}/oauth2/token`,
          params.toString(),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              Authorization: 'Basic YWlhbGlnbi13ZWI6YWlhbGlnbi13ZWItc2VjcmV0',
            },
          }
        );

        const data = response.data;
        if (data.access_token) {
          this.setAuthToken(data.access_token.replace(/^Bearer\s+/i, ''));
        }
        if (data.refresh_token) {
          this.setRefreshToken(data.refresh_token.replace(/^Bearer\s+/i, ''));
        }
        console.log('[ApiClient] Token 刷新成功');
      } finally {
        this.isRefreshing = false;
        this.refreshTokenPromise = null;
      }
    })();

    return this.refreshTokenPromise;
  }

  private handleUnauthorized() {
    // 处理未授权，比如清除 token，跳转到登录页
    this.clearAuthToken();

    // 检查是否为嵌入模式
    const isEmbedMode =
      typeof globalThis !== 'undefined' &&
      (globalThis as any).sessionStorage &&
      (globalThis as any).sessionStorage.getItem('intellix_embed_mode') ===
        'true';

    // 如果是嵌入模式，触发特殊事件让应用层处理（重定向到父项目）
    if (isEmbedMode) {
      if (
        typeof globalThis !== 'undefined' &&
        (globalThis as any).dispatchEvent
      ) {
        (globalThis as any).dispatchEvent(
          new CustomEvent('auth:embed-unauthorized')
        );
      }
      return;
    }

    // 非嵌入模式：触发标准未授权事件
    if (
      typeof globalThis !== 'undefined' &&
      (globalThis as any).dispatchEvent
    ) {
      (globalThis as any).dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
  }

  // 设置认证 token
  setAuthToken(token: string) {
    if (typeof globalThis !== 'undefined' && (globalThis as any).localStorage) {
      (globalThis as any).localStorage.setItem('auth_token', token);
    }
  }

  // 设置 refresh token
  setRefreshToken(token: string) {
    if (typeof globalThis !== 'undefined' && (globalThis as any).localStorage) {
      (globalThis as any).localStorage.setItem('refresh_token', token);
    }
  }

  // 清除认证 token
  clearAuthToken() {
    if (typeof globalThis !== 'undefined' && (globalThis as any).localStorage) {
      (globalThis as any).localStorage.removeItem('auth_token');
      (globalThis as any).localStorage.removeItem('refresh_token');
      (globalThis as any).localStorage.removeItem('user_info');
    }
  }

  // 登录
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      console.log('🚀 发起登录请求:', credentials.username);

      const response = await this.instance.post<LoginResponse>(
        `${this.authBaseURL}/oauth2/token`,
        credentials,
        { headers: this.authHeaders }
      );

      console.log('📨 登录响应:', response.data);

      // 检查响应状态
      if (response.data && response.data.access_token) {
        const { access_token: token, refresh_token, username } = response.data;

        console.log('✅ 登录成功，开始保存登录信息:', username);

        // 保存 token 和用户信息
        this.setAuthToken(token);
        if (refresh_token) this.setRefreshToken(refresh_token);
        if (
          typeof globalThis !== 'undefined' &&
          (globalThis as any).localStorage
        ) {
          (globalThis as any).localStorage.setItem(
            'user_info',
            JSON.stringify({ username })
          );
        }

        console.log('✅ Login successful:', username);
        return response.data;
      } else {
        console.log('❌ 登录失败，无法获取访问令牌');
        const error: ApiError = {
          code: -1,
          message: '登录失败，无法获取访问令牌',
        };
        throw error;
      }
    } catch (error: any) {
      console.error('❌ Login failed:', error);

      // 处理不同类型的错误
      if (error.response) {
        console.log('❌ 服务器响应错误:', error.response.data);
        const apiError: ApiError = {
          code: error.response.data?.code || error.response.status,
          message:
            error.response.data?.message || '登录失败，请检查用户名和密码',
        };
        throw apiError;
      } else if (error.code && error.message) {
        // 已经是 ApiError 类型
        console.log('❌ API错误:', error);
        throw error;
      } else {
        console.log('❌ 网络错误:', error);
        const networkError: ApiError = {
          code: 0,
          message: '网络错误，请棄查网络连接',
        };
        throw networkError;
      }
    }
  }

  // 登出
  async logout(): Promise<void> {
    try {
      // 可以调用后端登出接口（如果有的话）
      // await this.post('/api/auth/logout');
      await this.instance.post<LoginResponse>(
        `${this.authBaseURL}/api/auth/logout`
      );

      this.clearAuthToken();
      console.log('✅ Logout successful');

      // 触发登出事件
      if (
        typeof globalThis !== 'undefined' &&
        (globalThis as any).dispatchEvent
      ) {
        (globalThis as any).dispatchEvent(new CustomEvent('auth:logout'));
      }
    } catch (error) {
      console.error('❌ Logout error:', error);
      // 即使登出接口失败，也要清除本地 token
      this.clearAuthToken();
    }
  }

  // 获取当前用户信息
  getCurrentUser(): UserInfo | null {
    if (typeof globalThis !== 'undefined' && (globalThis as any).localStorage) {
      const userInfo = (globalThis as any).localStorage.getItem('user_info');
      if (userInfo) {
        try {
          return JSON.parse(userInfo);
        } catch {
          return null;
        }
      }
    }
    return null;
  }

  // 检查是否已登录
  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    return !!token;
  }

  // 添加请求拦截器
  addRequestInterceptor(interceptor: RequestInterceptor) {
    return this.instance.interceptors.request.use(interceptor);
  }

  // 添加响应拦截器
  addResponseInterceptor(
    interceptor: ResponseInterceptor,
    errorInterceptor?: ErrorInterceptor
  ) {
    return this.instance.interceptors.response.use(
      interceptor,
      errorInterceptor
    );
  }

  // GET 请求
  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  // POST 请求
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.post<ApiResponse<T>>(
      url,
      data,
      config
    );
    return response.data;
  }

  // PUT 请求
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  // DELETE 请求
  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  // PATCH 请求
  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.patch<ApiResponse<T>>(
      url,
      data,
      config
    );
    return response.data;
  }

  // 获取原始 axios 实例
  getInstance(): AxiosInstance {
    return this.instance;
  }
}

// 创建默认实例
export const createApiClient = (config: ApiClientConfig) =>
  new ApiClient(config);

// 网关基础地址：初始值用 env，请求拦截器会在每次请求前动态覆盖为 config.js 的值
const gatewayBaseURL =
  (globalThis as any)._intellix_server_config?.gatewayBaseUrl ||
  (globalThis as any)._template_app_server_config?.gatewayBaseUrl ||
  import.meta.env.VITE_GATEWAY_BASE_URL ||
  'https://gateway-dev.aialign.cn';

export const apiClient = createApiClient({
  baseURL: `${gatewayBaseURL}/aialign-intellix-core`, // 基础业务相关
  timeout: 10000,
  authBaseURL: `${gatewayBaseURL}/aialign-auth-service`, // 认证相关
  authHeaders: {
    'Content-Type': 'application/x-www-form-urlencoded',
    Authorization: 'Basic YWlhbGlnbi13ZWI6YWlhbGlnbi13ZWItc2VjcmV0',
  },
});
